import { useDebouncedState } from "@react-hookz/web";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Icon, IconButton, Loading, Spinner, Tooltip, Typography } from "@/components/atoms";
import { EmptyState } from "@/components/molecules";
import { useOperator } from "@/hooks";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazySearchDriverQuery } from "@/redux/apis/typeahead/typeaheadApi";
import {
  useGetVehicleAccessQuery,
  useGrantVehicleAccessMutation,
  useRemoveVehicleAccessMutation,
  useUpdateVehicleAccessMutation,
} from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, getAssetUrl } from "@/utils";
import { SelectedDriver } from "../schemas";
import { RemoveDriverModal } from "../modals/RemoveDriverModal";

interface Step2DriverAccessProps {
  vehicleId: string;
  vehicle: Vehicle;
  onNext: () => Promise<void>;
  onBack: () => void;
  onFinishLater: () => void;
}

type DriverOption = {
  id: string;
  name: string;
  avatar: string;
  email: string;
};

export const Step2DriverAccess = ({ vehicleId, vehicle, onNext, onBack, onFinishLater }: Step2DriverAccessProps) => {
  const operator = useOperator();
  const [updateVehicleAccess] = useUpdateVehicleAccessMutation();
  const [grantVehicleAccess] = useGrantVehicleAccessMutation();
  const [removeVehicleAccess] = useRemoveVehicleAccessMutation();

  // Load existing access for "Continue" flow
  const { data: existingAccess, isLoading: isLoadingAccess } = useGetVehicleAccessQuery({
    vehicleId,
    exclude_network_drivers: true,
    exclude_external_suppliers: true,
  });

  const [accessLevel, setAccessLevel] = useState<"RESTRICTED" | "ALL">(vehicle.accessType === "ALL" ? "ALL" : "RESTRICTED");
  const [drivers, setDrivers] = useState<SelectedDriver[]>(() => {
    if (!existingAccess) return [];
    return existingAccess
      .filter((a) => a.role === "VIEWER")
      .map((a) => ({ id: a.account.uuid, name: a.account.name, email: a.account.email, avatar: a.account.avatar }));
  });
  const [pendingRemove, setPendingRemove] = useState<SelectedDriver | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [query, setQuery] = useDebouncedState("", 300);
  const [searchDriver, { isFetching, data }] = useLazySearchDriverQuery();
  const [value, setValue] = useState<DriverOption | null>(null);

  // Update drivers when access data loads for the first time
  useEffect(() => {
    if (existingAccess && existingAccess.length > 0 && drivers.length === 0) {
      setDrivers(
        existingAccess
          .filter((a) => a.role === "VIEWER")
          .map((a) => ({ id: a.account.uuid, name: a.account.name, email: a.account.email, avatar: a.account.avatar }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAccess]);

  useEffect(() => {
    if (query.length > 1) searchDriver({ q: query });
  }, [query, searchDriver]);

  const options = useMemo(() => {
    return data?.map((i) => ({
      id: i.id,
      name: i.name,
      avatar: i.avatar,
      email: i.email,
    })) as DriverOption[] | undefined;
  }, [data]);

  const handleSelectDriver = useCallback(
    (option: DriverOption | null) => {
      setValue(option);
      if (!option) return;

      const alreadyAdded = drivers.some((d) => d.id === option.id);
      if (alreadyAdded) return;

      setDrivers((prev) => [...prev, { id: option.id, name: option.name, email: option.email, avatar: option.avatar }]);
      setValue(null);
    },
    [drivers]
  );

  const handleRemoveDriver = () => {
    if (!pendingRemove) return;
    setDrivers((prev) => prev.filter((d) => d.id !== pendingRemove.id));
    setPendingRemove(null);
  };

  const handleNext = async () => {
    setIsSaving(true);
    try {
      // Remove existing viewers that are no longer in the list
      if (existingAccess) {
        const currentDriverIds = new Set(drivers.map((d) => d.id));
        for (const access of existingAccess.filter((a) => a.role === "VIEWER")) {
          if (!currentDriverIds.has(access.account.uuid)) {
            await removeVehicleAccess({ vehicleId, accessId: access.uuid }).unwrap();
          }
        }
      }

      // Grant access to new drivers
      if (accessLevel === "RESTRICTED" && drivers.length > 0) {
        const existingIds = new Set(existingAccess?.filter((a) => a.role === "VIEWER").map((a) => a.account.uuid) ?? []);
        const newDrivers = drivers.filter((d) => !existingIds.has(d.id));
        if (newDrivers.length > 0) {
          await grantVehicleAccess({
            vehicleId,
            access: newDrivers.map((driver) => ({ identifier: driver.id, role: "VIEWER" as const })),
          }).unwrap();
        }
      }

      // Set access level + advance setup_step in a single call after all operations succeed
      // Only advance setup_step if on this step for the first time
      await updateVehicleAccess({ id: vehicleId, access: accessLevel, ...(vehicle.setupStep === 2 && { setup_step: 3 }) }).unwrap();

      await onNext();
    } catch (e: unknown) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingAccess) return <Loading />;

  return (
    <>
      <div className="flex flex-col gap-6">
        <div>
          <Typography variant="action">Select Access Level</Typography>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAccessLevel("RESTRICTED")}
              className={`rounded-lg border-2 p-4 text-center transition-colors ${
                accessLevel === "RESTRICTED" ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray"
              }`}
            >
              <Typography variant="action" className={accessLevel === "RESTRICTED" ? "text-primary" : ""}>
                Restricted
              </Typography>
              <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
                Only drivers with access can use this vehicle
              </Typography>
            </button>
            <button
              type="button"
              onClick={() => setAccessLevel("ALL")}
              className={`rounded-lg border-2 p-4 text-center transition-colors ${
                accessLevel === "ALL" ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray"
              }`}
            >
              <Typography variant="action" className={accessLevel === "ALL" ? "text-primary" : ""}>
                All Drivers
              </Typography>
              <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
                All drivers within {operator.name} can use this vehicle
              </Typography>
            </button>
          </div>
        </div>

        {accessLevel === "RESTRICTED" && (
          <>
            <div>
              <Typography variant="action" className="text-neutral-dark-gray">
                Enter the driver&apos;s email to share this vehicle for bookings
              </Typography>
              <Autocomplete
                options={options}
                isLoading={isFetching}
                value={value}
                onValueChange={handleSelectDriver}
                onQueryChange={setQuery}
                className="mt-2"
                placeholder="Search and add driver"
              >
                <Autocomplete.Options className="mt-2 max-h-72 w-full overflow-auto rounded-md border border-neutral-mid-gray bg-white text-base shadow-lg empty:mt-0">
                  {options?.map((i, idx) => {
                    const isAdded = drivers.some((d) => d.id === i.id);
                    return (
                      <Autocomplete.Option
                        key={idx}
                        value={i}
                        className={`flex w-full cursor-pointer px-4 pb-3 pt-4 hover:bg-primary-light ${
                          isAdded ? "pointer-events-none cursor-not-allowed" : ""
                        }`}
                      >
                        <div className="flex-1">
                          <Typography className={`leading-6 ${isAdded ? "text-neutral-dark-gray" : "text-neutral-black"}`}>
                            {i.name}
                          </Typography>
                          <Typography variant="small" className="text-neutral-dark-gray">
                            {i.email}
                          </Typography>
                        </div>
                        {isAdded && (
                          <div className="flex shrink-0 items-center justify-end">
                            <Icon name="TickCircle" variant="Bold" className="text-success" />
                          </div>
                        )}
                      </Autocomplete.Option>
                    );
                  })}
                </Autocomplete.Options>
              </Autocomplete>
            </div>

            <div>
              <Typography variant="action">Drivers with Access ({drivers.length})</Typography>
              <div className="mt-3 flex flex-col gap-2.5">
                {drivers.length > 0 ? (
                  drivers.map((driver) => (
                    <div key={driver.id} className="flex items-center gap-3 rounded-lg bg-neutral-surface-gray px-3 py-3">
                      <img
                        src={getAssetUrl(driver.avatar, { height: 80, width: 80, background: "transparent" })}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                      <div className="flex flex-1 flex-col">
                        <Typography className="text-neutral-black">{driver.name}</Typography>
                        <Typography variant="small" className="text-neutral-dark-gray">
                          {driver.email}
                        </Typography>
                      </div>
                      <Tooltip content="Remove this driver's access to this vehicle" placement="left">
                        <IconButton
                          iconName="close"
                          isCustomIcon
                          variant="custom"
                          iconSize="lg"
                          className="text-neutral-dark-gray"
                          onClick={() => setPendingRemove(driver)}
                        />
                      </Tooltip>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    title="Nothing here... yet"
                    description="Search and add a driver to share this vehicle to"
                    hideImage
                    className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray"
                  />
                )}
              </div>
            </div>
          </>
        )}

        {accessLevel === "ALL" && (
          <EmptyState
            title={`All drivers within ${operator.name} will have access to this vehicle`}
            description="You can manage individual driver access after the vehicle has been created."
            hideImage
            className="rounded-lg bg-neutral-surface-gray"
          />
        )}

        <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
          <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" onClick={onFinishLater}>
            Finish later
          </Button>
          <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
            <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
              Back
            </Button>
            <Button type="button" variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight" onClick={handleNext} disabled={isSaving}>
              {isSaving ? <Spinner /> : "Next"}
            </Button>
          </div>
        </div>
      </div>

      <RemoveDriverModal
        open={!!pendingRemove}
        driverName={pendingRemove?.name ?? ""}
        onClose={() => setPendingRemove(null)}
        onRemove={handleRemoveDriver}
      />
    </>
  );
};
